import { ReactNode, useMemo } from 'react';

import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ApolloProvider } from '@apollo/client/react';

import { useAuthentication } from '@freelancelabs/components';

interface Props {
    children: ReactNode;
}

/**
 * Apollo client for client-hub-api.
 *
 * Two deliberate differences with the one in `applications/client-hub`, which talks
 * to the previous API:
 *
 * - the client is built **once** (`useMemo`). Creating it in the component body
 *   returns a new client on every render, which throws the cache away each time;
 * - **no `typePolicies`**. This schema paginates with `limit`/`offset` and
 *   exposes no Relay connection, so `relayStylePagination` would name fields
 *   that do not exist.
 *
 * The token is read inside the link, not captured in a closure: it is refreshed
 * by Cognito and must be read at request time.
 */
const ApiProvider = ({ children }: Props) => {
    const { getAuthorizationToken } = useAuthentication();

    const client = useMemo(() => {
        const httpLink = new HttpLink({ uri: import.meta.env.PUBLIC_CLIENT_API_URL });

        const authLink = setContext(async (_, { headers }) => {
            const token = await getAuthorizationToken();
            return {
                headers: {
                    ...headers,
                    authorization: token ? `Bearer ${token}` : '',
                },
            };
        });

        return new ApolloClient({ link: authLink.concat(httpLink), cache: new InMemoryCache() });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Reason: le client ne doit etre construit qu'une fois ; getAuthorizationToken est lu a chaque requete par le link
    }, []);

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApiProvider;
